import { categories } from "@r8er/lib/config/categories";
import Link from "next/link";
import type { PropsWithChildren } from "react";
import { useId, useState } from "react";
import { Button, ButtonContainer } from "../button";
import { Container } from "../container";
import classes from "./categories.module.css";

/**
 * One entry in the Health / Climate switch. These are links rather than local
 * state so the selected sector is deep-linkable and other pages can point
 * straight at one taxonomy.
 */
interface CategorySectorLink {
  label: string;
  href: string;
  isActive: boolean;
}

interface CategoriesProps {
  title?: string;
  description: string;
  prompt?: string;
  limit?: number;
  visibleCategories?: typeof categories;
  sectorLinks?: Array<CategorySectorLink>;
  preserveSectorScroll?: boolean;
}

const IconUp = (
  <svg
    aria-hidden="true"
    focusable="false"
    xmlns="http://www.w3.org/2000/svg"
    width="49"
    height="49"
  >
    <circle cx="24.5" cy="24.5" r="24.5" fill="#00325a" />
    <g
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
    >
      <path d="M24.573 37.565V11.582" />
      <path d="M11.582 24.573l12.991-12.991 12.992 12.991" />
    </g>
  </svg>
);

const Categories = ({
  title,
  description,
  prompt = "Use the arrow buttons to find out more!",
  limit,
  visibleCategories = categories,
  sectorLinks,
  preserveSectorScroll = false,
}: PropsWithChildren<CategoriesProps>) => {
  const categoryIdPrefix = useId();
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  if (limit) {
    visibleCategories = visibleCategories.slice(0, limit);
  }

  const CategoryBlocks = visibleCategories.map((category, categoryIndex) => {
    const categoryKey = category.title;
    const descriptionId = `${categoryIdPrefix}-description-${categoryIndex + 1}`;
    const isExpanded = expandedCategories[categoryKey] === true;
    const accessibleTitle = category.title
      .replace(/<br\s*\/?\s*>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();

    return (
      <div
        key={`category_${category.title}`}
        className={classes.marketing__categories_grid_item}
        style={{ backgroundImage: `url(${category.imageURL})` }}
      >
        <button
          type="button"
          className={classes.marketing__categories_grid_item_toggle}
          aria-label={`${isExpanded ? "Hide" : "Show"} details for ${accessibleTitle}`}
          aria-expanded={isExpanded}
          aria-controls={descriptionId}
          onClick={() =>
            setExpandedCategories((current) => ({
              ...current,
              [categoryKey]: !current[categoryKey],
            }))
          }
        >
          {IconUp}
        </button>
        <div className={classes.marketing__categories_grid_item_content}>
          <strong dangerouslySetInnerHTML={{ __html: category.title }}></strong>
          <p id={descriptionId}>{category.description}</p>
        </div>
      </div>
    );
  });

  const MaybeSectorSwitch = sectorLinks?.length ? (
    <div
      className={classes.marketing__categories_domains}
      role="navigation"
      aria-label="Choose a category sector"
    >
      {sectorLinks.map((sectorLink) => (
        <Link
          key={sectorLink.href}
          href={sectorLink.href}
          scroll={!preserveSectorScroll}
          aria-current={sectorLink.isActive ? "page" : undefined}
          className={[
            classes.marketing__categories_domain,
            sectorLink.isActive
              ? classes.marketing__categories_domain_active
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {sectorLink.label}
        </Link>
      ))}
    </div>
  ) : null;

  const MaybeShowMore = limit && (
    <ButtonContainer>
      <Button
        buttonText="See More"
        buttonHref="/categories/health"
        isBlue
        isWide
      />
    </ButtonContainer>
  );

  return (
    <div className={classes.marketing__categories}>
      {title && <h2>{title}</h2>}
      <p className={classes.marketing__categories_description}>{description}</p>
      {MaybeSectorSwitch}
      <div className={classes.marketing__categories_prompt}>{prompt}</div>
      <Container>
        <div className={classes.marketing__categories_grid}>
          {CategoryBlocks}
        </div>
        {MaybeShowMore}
      </Container>
    </div>
  );
};

export { Categories };
